import React from "react"
import {
  Alert, Box, Chip, Paper, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Typography,
} from "@mui/material"

const normalize = (key) => key.replace(/[_\s-]/g, "").toLowerCase()
const labels = {
  filename: "File name", rownumber: "File row", terminalsn: "Terminal serial",
  terminalserial: "Terminal serial", serialnumber: "Terminal serial",
  error: "Issue", errormessage: "Issue", errorreason: "Issue",
  totalrows: "Total rows", processedrows: "Processed rows",
  totalread: "Total Terminals", totalwrite: "Successfully Imported", totalskip: "Not Imported",
  successcount: "Successful", successrows: "Successful rows",
  failedrows: "Failed rows", errorcount: "Errors", importid: "Import ID",
}
const label = (key) => labels[normalize(key)] || key
  .replace(/([a-z0-9])([A-Z])/g, "$1 $2").replace(/[_-]/g, " ")
  .toLowerCase().replace(/^./, (letter) => letter.toUpperCase())
const format = (value) => {
  if (value === null || value === undefined || value === "") return "—"
  if (typeof value === "boolean") return value ? "Yes" : "No"
  if (Array.isArray(value)) return value.map(format).join("; ") || "—"
  if (typeof value === "object") return Object.entries(value)
    .map(([key, item]) => `${label(key)}: ${format(item)}`).join(" · ") || "—"
  return String(value)
}
const statusColor = (value) => {
  const status = String(value).toUpperCase()
  if (/ERROR|FAIL|CANCEL/.test(status)) return "error"
  if (/PARTIAL|WARNING/.test(status)) return "warning"
  if (["COMPLETED", "COMPLETE", "SUCCESS", "SUCCEEDED", "DONE"].includes(status)) return "success"
  return "info"
}

// Strip transport metadata only from the response envelope. Import records may
// have their own meaningful fields (including counts and error codes).
const envelopeFields = new Set(["code", "response", "description", "type", "page", "perpage", "total", "totalpages"])
const recordsFrom = (value) => {
  const payload = value?.dataList ?? value?.data ?? value
  if (Array.isArray(payload)) return payload
  if (!payload || typeof payload !== "object") return []
  const record = payload === value
    ? Object.fromEntries(Object.entries(payload).filter(([key]) => !envelopeFields.has(normalize(key))))
    : payload
  return Object.keys(record).length ? [record] : []
}

const TerminalImportResults = ({ value, errors = false }) => {
  const records = recordsFrom(value).map((record) => record && typeof record === "object"
    ? Object.fromEntries(Object.entries(record).filter(([key]) => !["importid", "terminalimportid"].includes(normalize(key))))
    : { message: record }).filter((record) => Object.keys(record).length > 0)
  const message = typeof value === "string" ? value : value?.description
  const meaningfulMessage = typeof message === "string" && !["success", "ok"].includes(message.trim().toLowerCase())
  const columns = [...new Set(records.flatMap((record) => Object.keys(record)))]
    .sort((a, b) => {
      const rank = (key) => ["rownumber", "terminalsn", "terminalserial", "serialnumber"].indexOf(normalize(key))
      return (rank(a) < 0 ? 10 : rank(a)) - (rank(b) < 0 ? 10 : rank(b))
    })

  if (errors) return (
    <Stack spacing={2}>
      {meaningfulMessage && <Alert severity="info">{message}</Alert>}
      {records.length === 0 ? <Alert severity="info">No errors returned for this page. Check the import status to confirm whether processing has finished.</Alert> : <>
        <Typography variant="body2" color="text.secondary">
          {records.length} error {records.length === 1 ? "entry" : "entries"} shown. Review the issues below before correcting and importing affected terminals again.
        </Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360 }}>
          <Table size="small" stickyHeader aria-label="Terminal import errors">
            <TableHead><TableRow>{columns.map((key) => <TableCell key={key} sx={{ fontWeight: 600 }}>{label(key)}</TableCell>)}</TableRow></TableHead>
            <TableBody>{records.map((record, index) => <TableRow key={index} sx={{ "&:nth-of-type(even)": { bgcolor: "action.hover" } }}>
              {columns.map((key) => <TableCell key={key} sx={{ verticalAlign: "top", overflowWrap: "anywhere", minWidth: 100, maxWidth: 420, whiteSpace: "pre-wrap" }}>{format(record[key])}</TableCell>)}
            </TableRow>)}</TableBody>
          </Table>
        </TableContainer>
      </>}
    </Stack>
  )

  return <Stack spacing={2}>
    {meaningfulMessage && <Alert severity="info">{message}</Alert>}
    {records.length === 0 && !meaningfulMessage && <Alert severity="info">The request was received, but no import details were returned. Check the status again for an update.</Alert>}
    {records.map((record, index) => {
      const fileKey = Object.keys(record).find((key) => normalize(key) === "filename")
      const statusKey = Object.keys(record).find((key) => ["status", "importstatus"].includes(normalize(key)))
      const fields = Object.entries(record).filter(([key]) => key !== fileKey && key !== statusKey)
      return <Paper variant="outlined" key={index} sx={{ p: 2.5, borderRadius: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" useFlexGap flexWrap="wrap" spacing={1} sx={{ mb: fields.length ? 2 : 0 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ overflowWrap: "anywhere" }}>{fileKey ? format(record[fileKey]) : "Import summary"}</Typography>
          {statusKey && <Chip label={label(format(record[statusKey]))} color={statusColor(record[statusKey])} size="small" />}
        </Stack>
        <Box component="dl" sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" }, gap: 2, m: 0 }}>
          {fields.map(([key, item]) => <Box key={key} sx={{ p: 1.5, bgcolor: "action.hover", borderRadius: 1 }}>
            <Typography component="dt" variant="body2" color="text.secondary">{label(key)}</Typography>
            <Typography component="dd" sx={{ m: 0, mt: 0.5, fontWeight: 600, overflowWrap: "anywhere", whiteSpace: "pre-wrap" }}>{format(item)}</Typography>
          </Box>)}
        </Box>
      </Paper>
    })}
  </Stack>
}

export default TerminalImportResults
